/**
 * Grace day tracker.
 *
 * Grace days are unconditional: 1 free miss per calendar week per habit.
 * They protect the streak counter only — score still decays normally on the missed day.
 * A "backfill" completion retroactively fills a grace day (no longer counts as used).
 */

import { addDays, compareDates } from "./day-boundary";
import { getWeekBounds } from "./schedule";

import type { CompletionRecord } from "./types";

/**
 * Count how many grace days have been used in the calendar week containing `date`.
 *
 * A grace day is used when:
 * 1. The date is a due date (in `dueDates`)
 * 2. There is no completion record (done/backfill) for that date
 * 3. There is no skip record for that date
 *
 * In other words: a due date with no action taken = grace day consumed (if available).
 */
export function getGraceDaysUsedThisWeek(
  completions: CompletionRecord[],
  dueDates: string[],
  date: string,
  firstDayOfWeek: number,
): number {
  const week = getWeekBounds(date, firstDayOfWeek);
  const completionSet = buildCompletionDateSet(completions);
  const skipSet = buildSkipDateSet(completions);
  const dueDateSet = new Set(dueDates);

  let missedDueDates = 0;
  let current = week.start;

  while (compareDates(current, date) <= 0 && compareDates(current, week.end) <= 0) {
    if (dueDateSet.has(current) && !completionSet.has(current) && !skipSet.has(current)) {
      missedDueDates++;
    }
    current = addDays(current, 1);
  }

  // Grace days used = missed due dates capped at the allowance (1 per week)
  return Math.min(missedDueDates, 1);
}

/**
 * Check whether a grace day is available for the given date.
 * Returns true if no grace days have been used earlier in this calendar week.
 * Does NOT count the query date itself — this checks if grace is available FOR that date.
 */
export function isGraceDayAvailable(
  completions: CompletionRecord[],
  dueDates: string[],
  date: string,
  firstDayOfWeek: number,
): boolean {
  const week = getWeekBounds(date, firstDayOfWeek);
  const completionSet = buildCompletionDateSet(completions);
  const skipSet = buildSkipDateSet(completions);
  const dueDateSet = new Set(dueDates);

  let missedBefore = 0;
  let current = week.start;

  // Count missed due dates strictly before the query date
  while (compareDates(current, date) < 0 && compareDates(current, week.end) <= 0) {
    if (dueDateSet.has(current) && !completionSet.has(current) && !skipSet.has(current)) {
      missedBefore++;
    }
    current = addDays(current, 1);
  }

  return missedBefore < 1;
}

/**
 * Check if a specific date is a grace day (missed but protected).
 *
 * A date is a grace day if:
 * 1. It's a due date with no completion/skip
 * 2. A grace day was available at that point in the week
 */
export function isGraceDay(
  completions: CompletionRecord[],
  dueDates: string[],
  date: string,
  firstDayOfWeek: number,
): boolean {
  const dueDateSet = new Set(dueDates);
  if (!dueDateSet.has(date)) return false;

  const completionSet = buildCompletionDateSet(completions);
  const skipSet = buildSkipDateSet(completions);

  // If there's a completion or skip, it's not a grace day
  if (completionSet.has(date) || skipSet.has(date)) return false;

  // Count grace days used before this date in the same week
  const week = getWeekBounds(date, firstDayOfWeek);
  let graceDaysUsedBefore = 0;
  let current = week.start;

  while (compareDates(current, date) < 0) {
    if (dueDateSet.has(current) && !completionSet.has(current) && !skipSet.has(current)) {
      graceDaysUsedBefore++;
    }
    current = addDays(current, 1);
  }

  return graceDaysUsedBefore < 1;
}

/**
 * Build a set of dates that have a done/backfill completion.
 */
function buildCompletionDateSet(completions: CompletionRecord[]): Set<string> {
  const set = new Set<string>();
  for (const c of completions) {
    if (c.type === "done" || c.type === "backfill") {
      set.add(c.effective_date);
    }
  }
  return set;
}

/**
 * Build a set of dates that have a skip record.
 */
function buildSkipDateSet(completions: CompletionRecord[]): Set<string> {
  const set = new Set<string>();
  for (const c of completions) {
    if (c.type === "skip") {
      set.add(c.effective_date);
    }
  }
  return set;
}
